"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { BookOpen, Feather } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const authors = [{
  id: 1,
  name: "Marcus Sterling",
  role: "Senior Mortgage Advisor",
  bio: "With over 15 years in residential lending, Marcus specializes in complex refinancing and jumbo loan structures.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686387/img/advisor_marcus.png"
}, {
  id: 2,
  name: "Elena Rodriguez",
  role: "Home Equity Specialist",
  bio: "Elena helps homeowners unlock their property's potential through strategic equity lines and wealth management.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686387/img/advisor_elena.png"
}, {
  id: 3,
  name: "David Chen",
  role: "First-Time Buyer Expert",
  bio: "David is dedicated to guiding new buyers through the mortgage landscape with clarity and financial precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686387/img/advisor_david.png"
}];
const authorBooks = [{
  authorId: 1,
  title: "NMLS #482910"
}, {
  authorId: 1,
  title: "Call: (555) 012-3456"
}, {
  authorId: 2,
  title: "NMLS #773104"
}, {
  authorId: 2,
  title: "Call: (555) 012-7890"
}, {
  authorId: 3,
  title: "NMLS #921005"
}, {
  authorId: 3,
  title: "Call: (555) 012-4422"
}];
export default function YourGuidingTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221489" id="your-guiding-team" className="bg-background py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-[0.03]">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-lg bg-primary blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-lg bg-secondary blur-3xl" />
      </div>

      <div ref={ref} className={`container mx-auto px-6 md:px-8 max-w-7xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
          <div className="flex justify-center mb-4">
            <Feather className="size-8 text-primary/60" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Your Guiding Team
          </h2>
          <p className="text-lg text-foreground/70 leading-relaxed font-default">
            Meet the dedicated professionals at Beacon Finance Group. Our advisors combine deep market expertise with a commitment to your financial security.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {authors.map((author, i) => <Card key={author.id} data-index={i} className="relative pt-8 pb-4 overflow-hidden border-border/40 bg-card shadow-md hover:shadow-xl transition-all duration-500 group hover:-translate-y-1 rounded-md">
              <Badge variant="secondary" className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-primary text-primary-foreground border-transparent rounded-md">
                Expert Advisor
              </Badge>
              
              <CardHeader className="flex flex-col items-center text-center pb-4">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-md bg-primary/5 blur-md scale-110 group-hover:scale-125 transition-transform duration-500" />
                  <SafeHydrate><Avatar className="size-28 border-4 border-background shadow-sm relative z-10 rounded-md grayscale group-hover:grayscale-0 transition-all duration-700">
                    <AvatarImage src={author.image} alt={author.name} className="object-cover" />
                    <AvatarFallback className="font-default text-3xl bg-muted text-primary">
                      {author.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                </div>
                <CardTitle className="font-default text-2xl text-foreground mb-2 font-bold">
                  {author.name}
                </CardTitle>
                <CardDescription className="text-primary font-semibold tracking-wide text-sm uppercase font-mono">
                  {author.role}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex flex-col items-center text-center gap-8">
                <p className="text-sm text-foreground/80 leading-relaxed max-w-[280px] font-default">
                  {author.bio}
                </p>
                
                <div className="w-full pt-6 border-t border-border/30">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4 flex items-center justify-center gap-2 font-mono">
                    <span className="w-8 h-px bg-border/50" />
                    Credentials
                    <span className="w-8 h-px bg-border/50" />
                  </h4>
                  <ul className="flex flex-col gap-3">
                    {authorBooks.filter(b => b.authorId === author.id).map((book, j) => <li key={j} data-index={j} className="text-sm font-mono text-foreground flex items-start justify-center gap-2 group/book">
                          <BookOpen className="size-4 text-primary/40 mt-0.5 shrink-0 group-hover/book:text-secondary transition-colors" />
                          <span className="leading-tight font-medium">{book.title}</span>
                        </li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}